import {
  Box,
  Button,
  FormControl,
  Icon,
  MenuItem,
  Select,
  Tooltip,
  Typography,
  TextField,
} from "@material-ui/core";
import MaterialTable from "material-table";
import React, { useEffect, useState } from "react";
import ImageLoader from "react-load-image";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import defaultProfile from "../../../../assets/group.png";
import Preloader from "../../../../utilities/components/Preloader/Preloader";
import { MTableToolbar } from "material-table";
import {
  APP_URLS,
  PAGE_SIZE,
  PAGE_SIZE_OPTIONS,
  ROLES,
} from "../../../../utilities/constants";
import { getUserInfo } from "../../../../utilities/storage";
import { debounce } from "../../../../utilities/utilityFunctions";
import { getOrganisationsRequest } from "../../OrganisationsApiActions";
import { useStyles } from "./styles";
import { useCallback, useMemo } from "react";

/**
 * function to render organizations of a healthcare organization
 * @param history: history from router
 * @param getAllOrgs: function to get organizations
 * @param orgs: organizations list
 * @returns {JSX.Element}
 * @constructor
 */
const OrganizationsList = ({ history, getAllOrgs, orgs, headOrgId }) => {
  const classes = useStyles();
  const role = getUserInfo().Roles[0].name;
  const userInfo = getUserInfo();

  const headOrganizationId = headOrgId || userInfo?.headOrg?.id || 0;

  const [paginationParams, setPaginationParams] = useState({
    page: 0,
    rowsPerPage: PAGE_SIZE,
    searchData: {
      name: "",
      active: "",
    },
  });

  useEffect(() => {
    const payload = {
      ...paginationParams,
      headOrgId: headOrganizationId,
    };
    getAllOrgs(payload, true);
  }, [paginationParams]);

  /**
   * Handle status filter change
   */
  const handleStatusChange = (value) => {
    setPaginationParams((prev) => ({
      ...prev,
      page: 0,
      searchData: {
        ...prev.searchData,
        active: value,
      },
    }));
  };

  const columns = useMemo(
    () => [
      {
        field: "name",
        title: "Clinic Branch Name",
        filtering: true,
        cellStyle: { width: "25%" },
        headerStyle: { width: "25%" },
        render: ({ imageUrl, name, id }) => {
          return (
            <Box
              className={classes.profileContainer}
              component={Link}
              to={`${APP_URLS.ORGANIZATIONS}/${id}`} // ✅ Fixed
            >
              <Box className={classes.profileInnerContainer}>
                <ImageLoader src={imageUrl ? imageUrl : defaultProfile}>
                  <img alt={id} className={classes.profileImage} />
                  <div className={classes.profileImageError}>{id}</div>
                  <Preloader alt={id} className={classes.profileImage} />
                </ImageLoader>
              </Box>

              <Box className={classes.nameContainer}>
                <Typography classes={{ root: classes.name }}>{name}</Typography>
              </Box>
            </Box>
          );
        },
      },
      {
        field: "desc",
        title: "Description",
        filtering: false,
        cellStyle: { width: "auto" },
        headerStyle: { width: "auto" },
      },
      {
        field: "totalTeams",
        title: "Departments",
        filtering: false,
        cellStyle: { width: "10%", textAlign: "center" },
        headerStyle: { width: "10%", textAlign: "center" },
        render: ({ totalTeams }) => <>{totalTeams || 0}</>,
      },
      {
        field: "totalCaregiver",
        title: "Staff",
        filtering: false,
        cellStyle: { width: "10%", textAlign: "center" },
        headerStyle: { width: "10%", textAlign: "center" },
        render: ({ totalCaregiver }) => <>{totalCaregiver || 0}</>,
      },
      {
        field: "active",
        title: "Active Status",
        cellStyle: { width: "10%", textAlign: "center" },
        headerStyle: { width: "10%", textAlign: "center" },
        filtering: true,
        filterComponent: () => (
          <Box>
            <FormControl fullWidth>
              <Select
                value={paginationParams.searchData.active}
                onChange={(e) => handleStatusChange(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="1">Active</MenuItem>
                <MenuItem value="0">Blocked</MenuItem>
              </Select>
            </FormControl>
          </Box>
        ),
        render: ({ active }) =>
          active ? (
            <Tooltip arrow title="Active">
              <Icon className={classes.statusActive}>check_circle_outline</Icon>
            </Tooltip>
          ) : (
            <Tooltip arrow title="Blocked">
              <Icon className={classes.statusBlocked}>block</Icon>
            </Tooltip>
          ),
      },
    ],
    [paginationParams, classes]
  );
  const debouncedSearch = useMemo(
    () =>
      debounce((searchText, setPaginationParams) => {
        setPaginationParams((prev) => ({
          ...prev,
          page: 0,
          searchData: {
            ...prev.searchData,
            name: searchText,
          },
        }));
      }, 1000),
    []
  );
  /**
   * Function is called when user clicks add button
   */
  const onAdd = () => {
    history.push({
      pathname: APP_URLS.ORGANIZATIONS_ADD,
      state: headOrganizationId ? { headOrgId: headOrganizationId } : undefined,
    });
  };

  return (
    <Box className={classes.outerContainer}>
      <Box mt={1}>
        {(role === ROLES.ROLE_SYSTEM_ADMIN ||
          role === ROLES.ROLE_SUPER_ADMIN ||
          role === ROLES.ROLE_CLINIC_OWNER) && (
          <Box className={classes.addButtonContainer}>
            <Button
              variant="contained"
              color="primary"
              onClick={onAdd}
              startIcon={<Icon>add</Icon>}
              disableElevation
              classes={{ root: classes.addButton }}
            >
              Clinic
            </Button>
          </Box>
        )}

        <Box>
          <MaterialTable
            title="Clinic Locations"
            columns={columns}
            data={orgs.rows}
            options={{
              search: true,
              filtering: false,
              sorting: false,
              horizontalScroll: true,
              draggable: false,
              pageSizeOptions: PAGE_SIZE_OPTIONS,
              pageSize: paginationParams.rowsPerPage,
              emptyRowsWhenPaging: false,
              searchFieldAlignment: "left",
            }}
            onSearchChange={(searchText) => {
              debouncedSearch(searchText, setPaginationParams);
            }}
            totalCount={orgs.count}
            page={paginationParams.page}
            onChangeRowsPerPage={(pageSize) => {
              setPaginationParams((prev) => ({
                ...prev,
                rowsPerPage: pageSize,
              }));
            }}
            onChangePage={(page) => {
              setPaginationParams((prev) => ({
                ...prev,
                page,
              }));
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

const mapStateToProps = (state) => {
  return {
    orgs: state.organisations.orgs,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    getAllOrgs: (data, isHideLoader = false) => {
      return dispatch(getOrganisationsRequest(data, isHideLoader)).then(
        (res) => {
          return Promise.resolve(res);
        },
        (error) => {
          return Promise.reject(error);
        }
      );
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(OrganizationsList);
